<template>

    <div v-if="loading" class="flex flex-column justify-content-center align-items-center">
        <h4 class="text-center">Loading...</h4>
        <ScaleLoader class="m-auto" />
    </div>
    <div v-else>
        <div class="flex align-items-center">
            <div class="flex-grow-1 text-center">
                <h5>Document Preview</h5>
            </div>
            <Button icon="pi pi-file-pdf" label="Save as PDF" class="p-button-rounded p-button-help mr-2 mb-2" v-tooltip="'Save as PDF'" @click="printAsPdf" />
        </div>

        <div  v-if="answerData?.length > 0" class="grid">
            <div class="mx-auto">
                <div  class="card documentPreviewContainer flex flex-column my-2">
                   <div class="toPdf"> 
                    <div class="grid p-3">
                        <div class="col-2" v-if="author && author.id > 0">
                            Author:
                        </div>
                        <div class="col-4" v-if="author && author.id > 0">
                            <span class="user">{{author.name}}</span>&nbsp;
                            <span class="user">{{author.surname}}</span>
                        </div>
                        <div class="col-3" v-if="assessmentDate">
                            Assessment date:
                        </div>
                        <div class="col-3" v-if="assessmentDate">
                            {{assessmentDate}}
                        </div>
                        <div class="col-2" v-if="author && author.id > 0">
                            Selected User:
                        </div>
                        <div class="col-10" v-if="user && user.id > 0">
                            <span class="user">{{user.name}}</span>&nbsp;
                            <span class="user">{{user.surname}}</span>
                        </div>
                    </div>

                    <divider />

                    <div v-for="(answerStep, index) in answerData" :key="answerStep.stepId">
                        <hr v-if="index > 0" />
                        <div>{{answerStep.textBefore}}</div>

                        <ul class="list-none p-0 m-0">
                            <li v-for="attributeData in answerStep.detail" class=" surface-border flex justify-content-start gap-3 p-3" :key="attributeData.attributeId">
                                <div class="text-900 font-medium">
                                    {{ attributeData.label }} :
                                </div>
                                <div class="font-bold">
                                    {{ formatEntity(attributeData.answer, attributeData.attributeType) }}
                                    <span v-if="attributeData.unit && attributeData.unit.length > 0">
                                        ({{ attributeData.unit }})
                                    </span>
                                </div>
                            </li>
                        </ul>

                        <div>{{answerStep.textAfter}}</div>
                    </div>
                   </div>
                    <div class="pt-5 pb-5">
                        <h4 v-if="formPassFail === 1" class="passAssessment">
                            Pass
                        </h4>
                        <h4 v-else class="failAssessment">
                            Fail
                        </h4>
                    </div>
                </div>
            </div>
        </div>

        <div class="text-center mt-2">
            <strong v-if="answerData?.length > 0">
                Note: The document preview is provided for quick visual reference purposes only.
                Please note that the preview may differ from the actual document results upon downloading.
                For a detailed view of the results, it is recommended to
                download the document rather than relying solely on the preview.
            </strong>
        </div>
    </div>
</template>

<script setup>

import { ref, onMounted } from 'vue';
import GlobalRequest from '@/api/global-request';
import useUserComposable from '@/composables/UserComposable';
import { useRoute } from 'vue-router';
import { DateTime } from 'luxon';
import { showToast } from '@/helpers/toast-handler';
import { generatePdf } from '@/helpers/pdf-generator'; 

//import { useAnswerStore } from '../../products/pha/modules/answers/store';

const route = useRoute();
const assessmentCode = route.params.assessmentCode;
const formId = route.params.formId;

const formatEntity = (answer, answerType) => {

    if (answerType === '5') {
        const intPart = Math.trunc(Number(answer)); // returns 3
        const floatPart = Number(Number(answer) - intPart); //.toFixed(2)); // return 0.2

        if (floatPart !== 0) {
            return Number(answer).toFixed(2);
        }
    } else if (answerType === '7') {
        return DateTime.fromISO(answer).toLocaleString()
    }

    return answer;
}

const printAsPdf = () => {
    generatePdf({
        selector: '.toPdf',
        filename: 'assessment.pdf',
        applyStyles: true,
        showToast: showToast // pass your toast function
    }).catch(error => {
        console.error('PDF generation failed:', error);
    });
};




onMounted(async () => {
    await loadDocument();
});

const answerData = ref([]);
const formPassFail = ref([]);
const loading = ref(false);

const author = ref({});
const user = ref({});
const assessmentDate = ref(null);

const loadDocument = async () => {
    const { company_id } = useUserComposable()
    loading.value = true;

    GlobalRequest.getRequest('v2/assessment/form/' + assessmentCode + "/" + formId, {
        params: {
            company_id: company_id.value,
            output: 'json'
        }
    }).then((response) => {
        if (response.statusCode === 200) {

            formPassFail.value = response.data.result;

            if (Array.isArray(response.data.steps)) {
                answerData.value = response.data.steps;
                if (response.data.author) {
                    author.value = response.data.author
                }

                if (response.data.user) {
                    user.value = response.data.user
                }

                if (response.data.assessment_date) {
                    assessmentDate.value = DateTime
                        .fromISO(response.data.assessment_date)
                        .toLocaleString({
                            weekday: 'short',
                            month: 'short',
                            day: '2-digit',
                            year: 'numeric',
                        })
                }

                loading.value = false;

                //console.log('data', response.data.steps);
            }
        }
    }).catch((err) => {
        loading.value = false;
        showToast({}, err?.data?.message ?? 'Something went wrong');
        console.log(err)
    })



};
</script>

<style lang="scss" scoped>
.documentPreviewContainer {
    position: relative;
    width: 8.5in; /* Width of the PDF in inches */
    // height: 11in; /* Height of the PDF in inches */
    background-color: white;
}

.passAssessment {
    text-align: center;
    border: 5px solid rgb(75, 245, 56);
    background: rgb(82, 168, 87);
    border-radius: 15px;
    padding: 10px;
    color: white;
}

.failAssessment {
    text-align: center;
    border: 5px solid rgb(242, 41, 84);
    background: rgb(158, 41, 66);
    border-radius: 15px;
    padding: 10px;
    color: white;
}

.user {
    text-transform: capitalize;
    color: rgb(19, 90, 169);
}
</style>
